import { useState, useEffect, useCallback, Fragment } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Search, Plus, RefreshCw, Trash2, FileSpreadsheet, Download, Upload,
  Loader2, X, ChevronDown, ChevronRight, ArrowUpDown, Eye, Sparkles, Link2,
  Users, RotateCcw
} from 'lucide-react';
import { DatePickerCell } from '@/components/ui/date-picker';
import MacroLinkDropdown from '@/components/rollup/MacroLinkDropdown';
import CognaraMacroLinkModal from '@/components/construction/CognaraMacroLinkModal';
import UnifiedDailyGrid from '@/components/construction/UnifiedDailyGrid';
import ReforecastPreviewModal from '@/components/construction/ReforecastPreviewModal';

const STATUS_COLORS = {
  NOT_STARTED: { bg: '#64748b20', text: '#64748b' },
  IN_PROGRESS: { bg: '#3b82f620', text: '#3b82f6' },
  ON_TRACK: { bg: '#10b98120', text: '#10b981' },
  AHEAD: { bg: '#06b6d420', text: '#06b6d4' },
  BEHIND: { bg: '#ef444420', text: '#ef4444' },
  COMPLETED: { bg: '#22c55e20', text: '#22c55e' },
  SUSPENDED: { bg: '#f59e0b20', text: '#f59e0b' },
};

// ─────────────────────────────────────────────────────────────
// Resizable table header cell — drag the right edge to change width.
// Widths persist per-user via localStorage.
// ─────────────────────────────────────────────────────────────
const COL_WIDTH_STORE_KEY = 'fc-activity-col-widths-v1';

function useColWidths(defaults) {
  const [widths, setWidths] = useState(() => {
    try {
      const raw = localStorage.getItem(COL_WIDTH_STORE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return { ...defaults, ...parsed };
    } catch { return defaults; }
  });
  const setWidth = (key, w) => {
    setWidths(prev => {
      const next = { ...prev, [key]: Math.max(48, Math.round(w)) };
      try { localStorage.setItem(COL_WIDTH_STORE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };
  return [widths, setWidth];
}

function ResizeHandle({ onDrag }) {
  const onMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const th = e.currentTarget.parentElement;
    const startW = th.getBoundingClientRect().width;
    const onMove = (ev) => onDrag(startW + (ev.clientX - startX));
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  return (
    <span
      onMouseDown={onMouseDown}
      onClick={(e) => e.stopPropagation()}
      className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-500/40 active:bg-blue-500/70 transition-colors"
      title="Drag to resize"
      data-testid="col-resize-handle"
    />
  );
}


function AddActivityDialog({ projectId, onClose, onCreated }) {
  const [disciplines, setDisciplines] = useState([]);
  const [actTypes, setActTypes] = useState([]);
  const [form, setForm] = useState({
    activityName: '', disciplineCode: '', activityTypeCode: '', area: '', subArea: '',
    scopeQuantity: 100, planStartDate: '2025-03-01', planFinishDate: '2025-12-31',
    distributionCurveCode: 'BELL_CURVE', wbsCode: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/v1/projects/${projectId}/fc/disciplines`).then(r => setDisciplines(r.data)).catch(() => {});
    api.get(`/v1/projects/${projectId}/fc/activity-types`).then(r => setActTypes(r.data)).catch(() => {});
  }, [projectId]);

  const filteredTypes = form.disciplineCode ? actTypes.filter(t => t.disciplineCode === form.disciplineCode) : actTypes;

  const handleSave = async () => {
    if (!form.activityName || !form.disciplineCode || !form.activityTypeCode) {
      toast.error('Name, discipline, and activity type are required'); return;
    }
    setSaving(true);
    try {
      await api.post(`/v1/projects/${projectId}/fc/activities`, form);
      toast.success('Activity created');
      onCreated();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="add-activity-dialog">
      <div className="w-full max-w-lg rounded-xl p-6 shadow-xl space-y-4 max-h-[90vh] overflow-y-auto"
        style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Add FC Activity</h3>
          <button onClick={onClose}><X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Activity Name *</label>
            <input value={form.activityName} onChange={e => setForm({ ...form, activityName: e.target.value })}
              className="w-full px-3 py-2 rounded-md text-sm border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              data-testid="input-activity-name" />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Discipline *</label>
            <select value={form.disciplineCode} onChange={e => setForm({ ...form, disciplineCode: e.target.value, activityTypeCode: '' })}
              className="w-full px-3 py-2 rounded-md text-sm border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              data-testid="select-discipline">
              <option value="">Select...</option>
              {disciplines.map(d => <option key={d.disciplineCode} value={d.disciplineCode}>{d.disciplineCode} - {d.disciplineName}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Activity Type *</label>
            <select value={form.activityTypeCode} onChange={e => setForm({ ...form, activityTypeCode: e.target.value })}
              className="w-full px-3 py-2 rounded-md text-sm border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              data-testid="select-activity-type">
              <option value="">Select...</option>
              {filteredTypes.map(t => <option key={t.activityTypeCode} value={t.activityTypeCode}>{t.activityTypeCode} - {t.activityTypeName}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Area</label>
            <input value={form.area} onChange={e => setForm({ ...form, area: e.target.value })}
              className="w-full px-3 py-2 rounded-md text-sm border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Sub-Area</label>
            <input value={form.subArea} onChange={e => setForm({ ...form, subArea: e.target.value })}
              className="w-full px-3 py-2 rounded-md text-sm border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Scope Quantity *</label>
            <input type="number" value={form.scopeQuantity} onChange={e => setForm({ ...form, scopeQuantity: parseFloat(e.target.value) || 0 })}
              className="w-full px-3 py-2 rounded-md text-sm border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              data-testid="input-scope-qty" />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Distribution Curve</label>
            <select value={form.distributionCurveCode} onChange={e => setForm({ ...form, distributionCurveCode: e.target.value })}
              className="w-full px-3 py-2 rounded-md text-sm border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
              <option value="LINEAR">Linear</option>
              <option value="FRONT_LOADED">Front-Loaded</option>
              <option value="BACK_LOADED">Back-Loaded</option>
              <option value="BELL_CURVE">Bell Curve</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Plan Start *</label>
            <input type="date" value={form.planStartDate} onChange={e => setForm({ ...form, planStartDate: e.target.value })}
              className="w-full px-3 py-2 rounded-md text-sm border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              data-testid="input-plan-start" />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Plan Finish *</label>
            <input type="date" value={form.planFinishDate} onChange={e => setForm({ ...form, planFinishDate: e.target.value })}
              className="w-full px-3 py-2 rounded-md text-sm border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              data-testid="input-plan-finish" />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>WBS Code</label>
            <input value={form.wbsCode} onChange={e => setForm({ ...form, wbsCode: e.target.value })}
              className="w-full px-3 py-2 rounded-md text-sm border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-activity-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Activity'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function InlineDetail({ activity, projectId, onUpdated }) {
  const cp = activity.currentProgress || {};
  const dist = activity.plannedDistribution || [];
  const statusColor = STATUS_COLORS[activity.status] || STATUS_COLORS.NOT_STARTED;
  const [fcstStart, setFcstStart] = useState((activity.forecastStartDate || activity.planStartDate || '').slice(0, 10));
  const [fcstFinish, setFcstFinish] = useState((activity.forecastFinishDate || activity.planFinishDate || '').slice(0, 10));
  const [savingFcst, setSavingFcst] = useState(false);
  const [showRfPreview, setShowRfPreview] = useState(false);

  const generatePlan = async () => {
    try { await api.post(`/v1/projects/${projectId}/fc/activities/${activity.id}/generate-plan`); toast.success('Plan generated'); onUpdated(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const saveForecastDates = async () => {
    if (!fcstStart || !fcstFinish) { toast.error('Both forecast dates required'); return; }
    setSavingFcst(true);
    try {
      // 1) Persist the (possibly edited) forecast window
      await api.put(`/v1/projects/${projectId}/fc/activities/${activity.id}/forecast-dates`,
        { forecastStartDate: fcstStart, forecastFinishDate: fcstFinish });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); setSavingFcst(false); return; }
    setSavingFcst(false);
    // 2) Open smart re-forecast HITL preview for confirmation before applying
    setShowRfPreview(true);
  };

  return (
    <td colSpan={12}>
      <div className="px-6 py-4 space-y-3" style={{ background: 'var(--card-bg-alt)' }} data-testid={`detail-${activity.activityId}`}>
        {/* Info Grid */}
        <div className="grid grid-cols-4 lg:grid-cols-8 gap-3 text-xs">
          <div><span style={{ color: 'var(--text-muted)' }}>Discipline</span><p className="font-medium" style={{ color: 'var(--text-primary)' }}>{activity.disciplineCode} - {activity.disciplineName}</p></div>
          <div><span style={{ color: 'var(--text-muted)' }}>Type</span><p className="font-medium" style={{ color: 'var(--text-primary)' }}>{activity.activityTypeCode}</p></div>
          <div><span style={{ color: 'var(--text-muted)' }}>UOM</span><p className="font-medium" style={{ color: 'var(--text-primary)' }}>{activity.uom}</p></div>
          <div><span style={{ color: 'var(--text-muted)' }}>Scope Qty</span><p className="font-medium" style={{ color: 'var(--text-primary)' }}>{activity.scopeQuantity?.toLocaleString()}</p></div>
          <div><span style={{ color: 'var(--text-muted)' }}>MH Factor</span><p className="font-medium" style={{ color: 'var(--text-primary)' }}>{activity.manhourFactor}</p></div>
          <div><span style={{ color: 'var(--text-muted)' }}>Total MH</span><p className="font-medium" style={{ color: 'var(--text-primary)' }}>{activity.totalEquivalentManhours?.toLocaleString()}</p></div>
          <div><span style={{ color: 'var(--text-muted)' }}>Weightage</span><p className="font-medium" style={{ color: 'var(--text-primary)' }}>{activity.contributionWeightagePercent?.toFixed(2)}%</p></div>
          <div><span style={{ color: 'var(--text-muted)' }}>SPI</span><p className="font-medium" style={{ color: cp.spiQuantity >= 1 ? '#10b981' : cp.spiQuantity > 0 ? '#ef4444' : 'var(--text-muted)' }}>{cp.spiQuantity?.toFixed(3) || '-'}</p></div>
        </div>

        {/* Dates Row + Editable Forecast Range + Actual Dates + Cascade chip */}
        <div className="flex items-center gap-6 text-xs flex-wrap">
          <span style={{ color: 'var(--text-muted)' }}>Plan: <span className="font-mono" style={{ color: '#3b82f6' }}>{activity.planStartDate?.slice(0, 10)}</span> to <span className="font-mono" style={{ color: '#3b82f6' }}>{activity.planFinishDate?.slice(0, 10)}</span></span>
          <span style={{ color: 'var(--text-muted)' }}>Duration: <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{activity.durationDays} days</span></span>
          <span style={{ color: 'var(--text-muted)' }}>Curve: <span className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: 'var(--card-bg)', color: 'var(--accent-primary)' }}>{activity.distributionCurveCode}</span></span>
          {(activity.actualStartDate || activity.actualFinishDate) && (
            <span style={{ color: 'var(--text-muted)' }}>Actual:
              <span className="font-mono ml-1" style={{ color: '#10b981' }}>
                {activity.actualStartDate?.slice(0, 10) || '—'}
              </span>
              <span className="mx-1">→</span>
              <span className="font-mono" style={{ color: '#10b981' }}>
                {activity.actualFinishDate?.slice(0, 10) || '—'}
              </span>
            </span>
          )}
          <div className="flex items-center gap-1.5 px-2 py-1 rounded"
               style={{ background: 'var(--card-bg)', border: '1px dashed #f59e0b60' }}
               title="Forecast dates auto-cascade from the linked Macro Schedule task. Override here if needed.">
            {activity.forecastStartDate && activity.forecastFinishDate && (
              <span className="text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1"
                    style={{ background: '#22c55e18', color: '#22c55e', border: '1px solid #22c55e40' }}>
                <Link2 className="w-2.5 h-2.5" /> Cascaded from Macro
              </span>
            )}
            <span className="text-[10px] font-medium" style={{ color: '#f59e0b' }}>Forecast:</span>
            <input type="date" value={fcstStart}
                   onChange={e => setFcstStart(e.target.value)}
                   className="px-1 py-0.5 rounded text-[10px] font-mono border"
                   style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                   data-testid={`fc-forecast-start-${activity.activityId}`} />
            <span style={{ color: 'var(--text-muted)' }}>→</span>
            <input type="date" value={fcstFinish}
                   onChange={e => setFcstFinish(e.target.value)}
                   className="px-1 py-0.5 rounded text-[10px] font-mono border"
                   style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                   data-testid={`fc-forecast-finish-${activity.activityId}`} />
            <button onClick={saveForecastDates} disabled={savingFcst}
                    className="text-[10px] px-2 py-0.5 rounded font-medium disabled:opacity-50"
                    style={{ background: '#f59e0b', color: '#fff' }}
                    data-testid={`fc-forecast-save-${activity.activityId}`}>
              {savingFcst ? '…' : 'Save & Re-forecast'}
            </button>
            {typeof activity.forecastBalanceScope === 'number' && (
              <span className="text-[9px] ml-1 cursor-help"
                    style={{ color: 'var(--text-muted)' }}
                    title={activity.forecastBalanceScope <= 0
                      ? 'Actual quantity to date has fully consumed the scope. Any new work will re-open the balance.'
                      : `Remaining scope of ${activity.forecastBalanceScope.toFixed(1)} ${activity.uom || 'units'} to be executed over the forecast window.`}
                    data-testid={`fc-forecast-balance-${activity.activityId}`}>
                Balance: <b style={{ color: '#f59e0b' }}>{activity.forecastBalanceScope.toFixed(1)}</b>
                {activity.forecastBalanceScope <= 0 && (
                  <span className="ml-1 text-[9px] px-1 rounded"
                        style={{ background: '#10b98120', color: '#10b981' }}>
                    fully consumed
                  </span>
                )}
              </span>
            )}
          </div>
          <span className="ml-auto px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: statusColor.bg, color: statusColor.text }}>{activity.status}</span>
        </div>

        {(dist.length === 0 && (activity.forecastDistribution || []).length === 0 && (activity.actualProgress || []).length === 0) ? (
          <Button size="sm" variant="outline" onClick={generatePlan} data-testid="generate-plan-btn">Generate Plan Distribution</Button>
        ) : (
          <UnifiedDailyGrid projectId={projectId}
                            activityId={activity.id || activity.activityId}
                            activity={activity} />
        )}

        {showRfPreview && (
          <ReforecastPreviewModal
            projectId={projectId}
            mode="single"
            activity={activity}
            onClose={() => setShowRfPreview(false)}
            onApplied={() => { setShowRfPreview(false); onUpdated(); }}
          />
        )}
      </div>
    </td>
  );
}

export default function FCActivityRegister({ projectId }) {
  const [activities, setActivities] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [discFilter, setDiscFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [disciplines, setDisciplines] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showCognaraLink, setShowCognaraLink] = useState(false);
  const [showResourceLoad, setShowResourceLoad] = useState(false);
  const [showBulkReforecast, setShowBulkReforecast] = useState(false);
  const [resourceLoading, setResourceLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showExcelMenu, setShowExcelMenu] = useState(false);
  const [sortField, setSortField] = useState('activityId');
  const [sortDir, setSortDir] = useState('asc');

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (discFilter) params.append('discipline', discFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (areaFilter) params.append('area', areaFilter);
      if (search) params.append('search', search);
      params.append('limit', '500');
      const { data } = await api.get(`/v1/projects/${projectId}/fc/activities?${params}`);
      setActivities(data.activities || []);
      setTotal(data.total || 0);
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId, discFilter, statusFilter, areaFilter, search]);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);
  useEffect(() => {
    api.get(`/v1/projects/${projectId}/fc/disciplines`).then(r => setDisciplines(r.data)).catch(() => {});
  }, [projectId]);

  const deleteActivity = async (id) => {
    if (!window.confirm('Delete this activity?')) return;
    try { await api.delete(`/v1/projects/${projectId}/fc/activities/${id}`); toast.success('Deleted'); fetchActivities(); }
    catch { toast.error('Delete failed'); }
  };

  const bulkGeneratePlans = async () => {
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/fc/activities/bulk-generate-plans`);
      toast.success(data?.message || 'Plans generated successfully');
      fetchActivities();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to generate plans';
      toast.error(msg);
    }
  };

  const downloadExport = async () => {
    try {
      const res = await api.get(`/v1/projects/${projectId}/fc/excel/full-export`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `FC_${projectId}_Register.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Register exported');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Export failed'); }
  };
  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/v1/projects/${projectId}/fc/excel/template`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'FC_Import_Template.xlsx'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Template failed'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/fc/excel/import`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const parts = [];
      if (data.activitiesUpserted) parts.push(`${data.activitiesUpserted} activities`);
      if (data.manpowerRows) parts.push(`${data.manpowerRows} manpower rows`);
      if (data.equipmentRows) parts.push(`${data.equipmentRows} equipment rows`);
      toast.success(`Imported ${parts.join(' · ') || '0 rows'}` + (data.errors?.length ? `, ${data.errors.length} errors` : ''));
      fetchActivities();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Import failed'); }
    e.target.value = '';
    setShowExcelMenu(false);
  };

  const areas = [...new Set(activities.map(a => a.area).filter(Boolean))];

  const sortedActivities = [...activities].sort((a, b) => {
    const aVal = a[sortField] ?? '';
    const bVal = b[sortField] ?? '';
    if (typeof aVal === 'number') return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    return sortDir === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortHeader = ({ field, children }) => (
    <th className="p-2 text-left font-medium cursor-pointer select-none relative overflow-hidden" style={{ color: 'var(--text-muted)' }} onClick={() => toggleSort(field)}>
      <span className="flex items-center gap-1 pr-2 truncate">{children} <ArrowUpDown className="w-2.5 h-2.5 opacity-40 flex-none" /></span>
      <ResizeHandle onDrag={(w) => setColW(field, w)} />
    </th>
  );

  const [colWidths, setColW] = useColWidths({
    activityId: 150, activityName: 260, disciplineCode: 70, activityTypeCode: 100,
    area: 220, scopeQuantity: 100, uom: 80, totalEquivalentManhours: 100,
    contributionWeightagePercent: 80, progress: 180, status: 120, macroLink: 130,
    contractor: 170,
  });

  const [cascadeLoading, setCascadeLoading] = useState(false);
  const runContractorCascade = async () => {
    if (!window.confirm('Cascade contractors from Battery Limits → Macro Schedule → F&C activities?\n\nThis will re-stamp the Contractor column on every linked macro task and its downstream F&C activities (rows manually overridden are preserved).')) return;
    setCascadeLoading(true);
    try {
      const macroRes = await api.post(`/v1/projects/${projectId}/contractors/cascade-macro`);
      const fcRes = await api.post(`/v1/projects/${projectId}/fc/contractors/cascade-macro`);
      toast.success(`Macro: ${macroRes.data.tasksStamped} tasks · F&C: ${fcRes.data.activitiesStamped} activities`);
      if (macroRes.data.warnings?.length) {
        toast.warning(`${macroRes.data.warnings.length} BL warning(s) — see console`);
        // eslint-disable-next-line no-console
        console.warn('Contractor cascade warnings:', macroRes.data.warnings);
      }
      fetchActivities();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Contractor cascade failed');
    } finally {
      setCascadeLoading(false);
    }
  };

  const resetContractorToMacro = async (activityId) => {
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/fc/${activityId}/contractor/reset`);
      toast.success(`Reset to macro: ${data.contractor || '—'}`);
      fetchActivities();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    }
  };

  return (
    <div className="space-y-3" data-testid="fc-activity-register">
      {/* Action Bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search activities..."
            className="w-full pl-9 pr-3 py-2 rounded-lg text-sm border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            data-testid="search-activities" />
        </div>
        <select value={discFilter} onChange={e => setDiscFilter(e.target.value)}
          className="px-2 py-2 rounded-lg text-xs border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
          data-testid="filter-discipline">
          <option value="">All Disciplines</option>
          {disciplines.map(d => <option key={d.disciplineCode} value={d.disciplineCode}>{d.disciplineCode}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-2 py-2 rounded-lg text-xs border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
          data-testid="filter-status">
          <option value="">All Statuses</option>
          {['NOT_STARTED', 'IN_PROGRESS', 'ON_TRACK', 'AHEAD', 'BEHIND', 'COMPLETED', 'SUSPENDED'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {areas.length > 0 && (
          <select value={areaFilter} onChange={e => setAreaFilter(e.target.value)}
            className="px-2 py-2 rounded-lg text-xs border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
            <option value="">All Areas</option>
            {areas.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{total} activities</span>
          <Button size="sm" variant="ghost" onClick={fetchActivities} data-testid="refresh-btn"><RefreshCw className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="outline" onClick={bulkGeneratePlans} data-testid="bulk-generate-btn">Generate Plans</Button>
          <Button size="sm" variant="outline"
                  onClick={() => setShowBulkReforecast(true)}
                  data-testid="bulk-reforecast-btn"
                  className="text-amber-500 border-amber-500/40 hover:bg-amber-500/10"
                  title="Recompute forecast distribution for every activity — past locked to actuals, balance re-spread from today to plan finish.">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> Save & Re-forecast All
          </Button>
          <Button size="sm" variant="outline" onClick={runContractorCascade} disabled={cascadeLoading}
                  data-testid="cascade-contractors-btn"
                  className="text-violet-500 border-violet-500/40 hover:bg-violet-500/10">
            {cascadeLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Users className="w-3.5 h-3.5 mr-1" />}
            Cascade Contractors from BL
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowCognaraLink(true)}
                  data-testid="cognara-auto-link-btn"
                  className="text-sky-500 border-sky-500/40 hover:bg-sky-500/10">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> Cognara: Auto-Link Macro
          </Button>
          <Button size="sm" variant="outline" onClick={async () => {
            if (!window.confirm('Bring resources from the Macro Schedule and distribute them across the linked F&C activities (weighted by scope × MH)?')) return;
            setResourceLoading(true);
            try {
              const { data } = await api.post(`/v1/projects/${projectId}/fc/resource-load-macro-schedule`, { regenerate: true });
              toast.success(`${data.tasksProcessed} macro task${data.tasksProcessed !== 1 ? 's' : ''} · ${data.macroResourcesUpserted} resources seeded · ${data.fcAllocationsWritten} F&C splits`);
              fetchActivities();
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Resource-load failed');
            } finally { setResourceLoading(false); }
          }} disabled={resourceLoading}
             data-testid="fc-resource-load-btn"
             className="text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/10">
            {resourceLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            Pull Macro Resources → F&C
          </Button>
          <div className="relative">
            <Button size="sm" variant="outline" onClick={() => setShowExcelMenu(!showExcelMenu)} data-testid="excel-menu-btn">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel
            </Button>
            {showExcelMenu && (
              <div className="absolute right-0 mt-1 w-48 rounded-lg shadow-xl border z-50 py-1" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
                <button onClick={downloadExport} className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:opacity-80" style={{ color: 'var(--text-primary)' }}>
                  <Download className="w-3.5 h-3.5" /> Export Activities
                </button>
                <button onClick={downloadTemplate} className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:opacity-80" style={{ color: 'var(--text-primary)' }}>
                  <Download className="w-3.5 h-3.5" /> Download Template
                </button>
                <label className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 cursor-pointer hover:opacity-80" style={{ color: 'var(--text-primary)' }}>
                  <Upload className="w-3.5 h-3.5" /> Import from Excel
                  <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
                </label>
              </div>
            )}
          </div>
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-activity-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Activity</Button>
        </div>
      </div>

      {/* Activity Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
      ) : (
        <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
          <table className="text-xs" style={{ tableLayout: 'fixed', minWidth: '100%' }} data-testid="activities-table">
            <colgroup>
              <col style={{ width: 32 }} />
              <col style={{ width: colWidths.activityId }} />
              <col style={{ width: colWidths.activityName }} />
              <col style={{ width: colWidths.disciplineCode }} />
              <col style={{ width: colWidths.activityTypeCode }} />
              <col style={{ width: colWidths.contractor }} />
              <col style={{ width: colWidths.area }} />
              <col style={{ width: colWidths.scopeQuantity }} />
              <col style={{ width: colWidths.uom }} />
              <col style={{ width: colWidths.totalEquivalentManhours }} />
              <col style={{ width: colWidths.contributionWeightagePercent }} />
              <col style={{ width: colWidths.progress }} />
              <col style={{ width: colWidths.status }} />
              <col style={{ width: colWidths.macroLink }} />
            </colgroup>
            <thead>
              <tr style={{ background: 'var(--card-bg-alt)' }}>
                <th className="p-2"></th>
                <SortHeader field="activityId">ID</SortHeader>
                <SortHeader field="activityName">Name</SortHeader>
                <SortHeader field="disciplineCode">Disc</SortHeader>
                <SortHeader field="activityTypeCode">Type</SortHeader>
                <th className="p-2 text-left font-medium relative overflow-hidden" style={{ color: 'var(--text-muted)' }}
                    title="Contractor (cascaded from Battery Limits). Click ↺ on a row to reset an override.">
                  <span className="pr-2 truncate block">Contractor</span>
                  <ResizeHandle onDrag={(w) => setColW('contractor', w)} />
                </th>
                <th className="p-2 text-left font-medium relative overflow-hidden" style={{ color: 'var(--text-muted)' }}>
                  <span className="pr-2 truncate block">Area</span>
                  <ResizeHandle onDrag={(w) => setColW('area', w)} />
                </th>
                <SortHeader field="scopeQuantity">Scope Qty</SortHeader>
                <th className="p-2 text-right font-medium relative overflow-hidden" style={{ color: 'var(--text-muted)' }}>
                  <span className="pr-2 truncate block">UOM</span>
                  <ResizeHandle onDrag={(w) => setColW('uom', w)} />
                </th>
                <SortHeader field="totalEquivalentManhours">Total MH</SortHeader>
                <SortHeader field="contributionWeightagePercent">Wt%</SortHeader>
                <th className="p-2 text-right font-medium relative overflow-hidden" style={{ color: 'var(--text-muted)' }}>
                  <span className="pr-2 truncate block">Progress</span>
                  <ResizeHandle onDrag={(w) => setColW('progress', w)} />
                </th>
                <th className="p-2 text-center font-medium relative overflow-hidden" style={{ color: 'var(--text-muted)' }}>
                  <span className="pr-2 truncate block">Status</span>
                  <ResizeHandle onDrag={(w) => setColW('status', w)} />
                </th>
                <th className="p-2 text-center font-medium relative overflow-hidden" style={{ color: 'var(--text-muted)' }}>
                  <span className="pr-2 truncate block">Macro Link</span>
                  <ResizeHandle onDrag={(w) => setColW('macroLink', w)} />
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedActivities.map(a => {
                const sc = STATUS_COLORS[a.status] || STATUS_COLORS.NOT_STARTED;
                const progPct = a.currentProgress?.ftmActualPct || 0;
                const isExpanded = expandedId === a.id;
                return (
                  <Fragment key={a.id}>
                    <tr className="border-t cursor-pointer hover:opacity-80" style={{ borderColor: 'var(--border-color)' }}
                      onClick={() => setExpandedId(isExpanded ? null : a.id)} data-testid={`activity-row-${a.activityId}`}>
                      <td className="p-2">
                        {isExpanded ? <ChevronDown className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /> : <ChevronRight className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />}
                      </td>
                      <td className="p-2 font-mono font-medium" style={{ color: 'var(--accent-primary)' }}>{a.activityId}</td>
                      <td className="p-2 max-w-[200px] truncate" style={{ color: 'var(--text-primary)' }}>{a.activityName}</td>
                      <td className="p-2" style={{ color: 'var(--text-muted)' }}>{a.disciplineCode}</td>
                      <td className="p-2" style={{ color: 'var(--text-muted)' }}>{a.activityTypeCode}</td>
                      <td className="p-2" onClick={(e) => e.stopPropagation()}>
                        {a.contractor ? (
                          <div className="flex items-center gap-1.5 group">
                            <span style={{
                              display: 'inline-block', width: 6, height: 6, borderRadius: 9999,
                              background: a.contractorSource === 'MACRO' ? '#10b981'
                                : a.contractorSource === 'MANUAL' ? '#f59e0b' : '#64748b',
                              flex: 'none',
                            }} title={`Source: ${a.contractorSource || 'unknown'}`} />
                            <span className="truncate text-[11px]" style={{ color: 'var(--text-primary)' }} title={a.contractor}>
                              {a.contractor}
                            </span>
                            {a.contractorSource === 'MANUAL' && (
                              <button
                                type="button"
                                className="opacity-60 hover:opacity-100 flex-none"
                                title="Reset to macro-derived value"
                                onClick={() => resetContractorToMacro(a.id)}
                                data-testid={`contractor-reset-${a.activityId}`}
                              >
                                <RotateCcw className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-[10px] italic" style={{ color: 'var(--text-disabled)' }}>—</span>
                        )}
                      </td>
                      <td className="p-2" style={{ color: 'var(--text-muted)' }}>{a.area || '-'}</td>
                      <td className="p-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{a.scopeQuantity?.toLocaleString()}</td>
                      <td className="p-2 text-right" style={{ color: 'var(--text-muted)' }}>{a.uom}</td>
                      <td className="p-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{a.totalEquivalentManhours?.toLocaleString()}</td>
                      <td className="p-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{a.contributionWeightagePercent?.toFixed(2)}%</td>
                      <td className="p-2 text-right">
                        <div className="flex items-center gap-1.5 justify-end">
                          <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border-color)' }}>
                            <div className="h-full rounded-full" style={{ width: `${Math.min(progPct, 100)}%`, background: sc.text }} />
                          </div>
                          <span className="font-mono" style={{ color: 'var(--text-primary)' }}>{progPct.toFixed(1)}%</span>
                        </div>
                      </td>
                      <td className="p-2 text-center">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: sc.bg, color: sc.text }}>{a.status}</span>
                      </td>
                      <td className="p-2 text-center">
                        <MacroLinkDropdown
                          projectId={projectId}
                          microModule="QPT"
                          microActivityId={a.id}
                          microActivityCode={a.activityId}
                          microActivityName={a.activityName}
                        />
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr><InlineDetail activity={a} projectId={projectId} onUpdated={fetchActivities} /></tr>
                    )}
                  </Fragment>
                );
              })}
              {sortedActivities.length === 0 && (
                <tr><td colSpan={14} className="p-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>No activities found. Add an activity or import from Excel.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && <AddActivityDialog projectId={projectId} onClose={() => setShowAdd(false)} onCreated={fetchActivities} />}
      {showCognaraLink && (
        <CognaraMacroLinkModal projectId={projectId}
                                onClose={() => setShowCognaraLink(false)}
                                onCommitted={fetchActivities} />
      )}
      {showBulkReforecast && (
        <ReforecastPreviewModal
          projectId={projectId}
          mode="bulk"
          onClose={() => setShowBulkReforecast(false)}
          onApplied={() => { setShowBulkReforecast(false); fetchActivities(); }}
        />
      )}
    </div>
  );
}
